import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../utils/queries/bloodCenterSiteQuery";
import fetchBloodCollectionDailyPlan, {
  createBloodCollectionDailyPlan,
  updateBloodCollectionDailyPlan,
  deleteBloodCollectionDailyPlan,
} from "../../../utils/queries/bloodCollectionDailyPlanQuery";
import fetchBloodDonation from "../../../utils/queries/bloodDonationQuery";
import { defineRoleCenterSite, hasAllCenters } from "../../../utils/handleAccessRoles";
import { getStorage } from "../../../utils/storage";

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - 2 + i;
  return { label: String(y), value: y };
});

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

class BloodCollectionDailyPlan extends React.Component {
  state = {
    isAdmin: false,
    roleCenterSite: null,
    roleCenter: null,

    calYear: currentYear,
    calMonth: currentMonth,

    filterCenter: null,
    filterCenterSite: null,
    centers: [],
    centerSites: [],

    plans: [],
    performed: {},
    isLoading: false,
    isLoadingPerformed: false,

    showModal: false,
    showDeleteModal: false,
    showDayModal: false,
    editRecord: null,
    deleteId: null,
    selectedDay: null,

    formCenter: null,
    formCenterSite: null,
    formCenterSites: [],
    formDate: moment().format("YYYY-MM-DD"),
    formPlannedSessions: "",
    formPlannedUnits: "",
    formError: {},
    isSaving: false,
    isDeleting: false,

    viewMode: "calendar",
  };

  async componentDidMount() {
    await this.loadRole();
    this.getCenters();
    this.fetchPlans();
  }

  loadRole = async () => {
    const { centerRole, centerSiteRole } = await defineRoleCenterSite();
    const user = await getStorage();
    const isSuperAdmin = user?.role === "superAdmin";
    const isCentral = hasAllCenters(user?.center);
    const isAdmin = isSuperAdmin || isCentral || (!centerRole && !centerSiteRole);
    this.setState({ isAdmin, roleCenter: centerRole, roleCenterSite: centerSiteRole });
  };

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
    this.setState({ centers: data || [] });
  };

  getCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ centerSites: data || [] });
  };

  getFormCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ formCenterSites: data || [] });
  };

  buildFetchParams = () => {
    const {
      calYear, calMonth,
      filterCenter, filterCenterSite,
      isAdmin, roleCenter, roleCenterSite,
    } = this.state;

    const params = {
      year: calYear,
      month: calMonth,
      limit: 500,
      page: 1,
      sortField: "date",
      sortOrder: 1,
    };

    if (isAdmin) {
      if (filterCenter?.value) params.center = filterCenter.value;
      if (filterCenterSite?.value) params.centerSite = filterCenterSite.value;
    } else {
      if (roleCenterSite) params.centerSite = roleCenterSite?._id || roleCenterSite;
      else if (roleCenter) params.center = roleCenter?._id || roleCenter;
    }

    return params;
  };

  fetchPlans = async () => {
    this.setState({ isLoading: true, performed: {} });
    try {
      const params = this.buildFetchParams();
      const res = await fetchBloodCollectionDailyPlan(this.props.defaultLanguage, params);
      const plans = res?.data || [];
      this.setState({ plans, isLoading: false });
      this.fetchPerformed(plans);
    } catch {
      this.setState({ isLoading: false });
    }
  };

  fetchPerformed = async (plans) => {
    if (!plans.length) return;
    this.setState({ isLoadingPerformed: true });

    // Separate site-level plans (have centerSite) from center-level plans (no centerSite)
    const uniqueSiteDates = {};   // siteId → Set<dateStr>
    const uniqueCenterDates = {}; // centerId → Set<dateStr>

    plans.forEach((p) => {
      const siteId = p.centerSite?._id || p.centerSite || null;
      const centerId = p.center?._id || p.center;
      const dateKey = moment(p.date).format("YYYY-MM-DD");

      if (siteId) {
        if (!uniqueSiteDates[siteId]) uniqueSiteDates[siteId] = new Set();
        uniqueSiteDates[siteId].add(dateKey);
      } else {
        if (!uniqueCenterDates[centerId]) uniqueCenterDates[centerId] = new Set();
        uniqueCenterDates[centerId].add(dateKey);
      }
    });

    const performed = {};

    await Promise.all([
      // Site-level: query by centerSite
      ...Object.entries(uniqueSiteDates).map(async ([siteId, dates]) => {
        await Promise.all(
          [...dates].map(async (dateStr) => {
            try {
              const res = await fetchBloodDonation(this.props.defaultLanguage, {
                centerSite: siteId,
                startDate: `${dateStr} 00:00`,
                endDate: `${dateStr} 23:59`,
                limit: 1,
                page: 1,
              });
              performed[`site_${siteId}_${dateStr}`] = res?.totalRecords || 0;
            } catch {
              performed[`site_${siteId}_${dateStr}`] = 0;
            }
          })
        );
      }),
      // Center-level: query by center (captures all donations at any site of that center)
      ...Object.entries(uniqueCenterDates).map(async ([centerId, dates]) => {
        await Promise.all(
          [...dates].map(async (dateStr) => {
            try {
              const res = await fetchBloodDonation(this.props.defaultLanguage, {
                center: centerId,
                startDate: `${dateStr} 00:00`,
                endDate: `${dateStr} 23:59`,
                limit: 1,
                page: 1,
              });
              performed[`center_${centerId}_${dateStr}`] = res?.totalRecords || 0;
            } catch {
              performed[`center_${centerId}_${dateStr}`] = 0;
            }
          })
        );
      }),
    ]);

    this.setState({ performed, isLoadingPerformed: false });
  };

  getPerformed = (plan) => {
    const siteId = plan.centerSite?._id || plan.centerSite || null;
    const centerId = plan.center?._id || plan.center;
    const dateStr = moment(plan.date).format("YYYY-MM-DD");
    const key = siteId
      ? `site_${siteId}_${dateStr}`
      : `center_${centerId}_${dateStr}`;
    return this.state.performed[key] ?? null;
  };

  navigateMonth = (delta) => {
    let { calYear, calMonth } = this.state;
    calMonth += delta;
    if (calMonth > 12) { calMonth = 1; calYear++; }
    if (calMonth < 1) { calMonth = 12; calYear--; }
    this.setState({ calYear, calMonth }, this.fetchPlans);
  };

  plansByDate() {
    const map = {};
    this.state.plans.forEach((p) => {
      const key = moment(p.date).format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }

  openDayModal = (dateStr, dayPlans) => {
    this.setState({ showDayModal: true, selectedDay: { dateStr, plans: dayPlans } });
  };

  closeDayModal = () => this.setState({ showDayModal: false, selectedDay: null });

  openAddModal = (prefillDate) => {
    const { isAdmin, roleCenter, roleCenterSite, centers } = this.state;
    let formCenter = null;
    let formCenterSite = null;

    if (!isAdmin) {
      if (roleCenterSite) {
        formCenterSite = { label: roleCenterSite?.name || roleCenterSite, value: roleCenterSite?._id || roleCenterSite };
      }
      if (roleCenter) {
        const found = centers.find((c) => c.value === (roleCenter?._id || roleCenter));
        formCenter = found || { label: roleCenter?.name || roleCenter, value: roleCenter?._id || roleCenter };
      }
    }

    this.setState({
      showModal: true,
      editRecord: null,
      formCenter,
      formCenterSite,
      formCenterSites: formCenterSite ? [formCenterSite] : [],
      formDate: prefillDate || moment().format("YYYY-MM-DD"),
      formPlannedSessions: "",
      formPlannedUnits: "",
      formError: {},
    });

    if (formCenter?.value && !formCenterSite) {
      this.getFormCenterSites(formCenter.value);
    }
  };

  openEditModal = (record) => {
    const formCenter = record.center
      ? { label: record.center.name || record.center._id, value: record.center._id || record.center }
      : null;
    const formCenterSite = record.centerSite
      ? { label: record.centerSite.name || record.centerSite._id, value: record.centerSite._id || record.centerSite }
      : null;

    this.setState({
      showModal: true,
      editRecord: record,
      formCenter,
      formCenterSite,
      formCenterSites: formCenterSite ? [formCenterSite] : [],
      formDate: moment(record.date).format("YYYY-MM-DD"),
      formPlannedSessions: String(record.plannedSessions ?? ""),
      formPlannedUnits: String(record.plannedUnits ?? ""),
      formError: {},
    });

    if (formCenter?.value) this.getFormCenterSites(formCenter.value);
  };

  closeModal = () => this.setState({ showModal: false, editRecord: null });

  validateForm = () => {
    const { formCenter, formDate, formPlannedSessions, formPlannedUnits } = this.state;
    const errors = {};
    if (!formCenter) errors.formCenter = "Required";
    if (!formDate) errors.formDate = "Required";
    if (!formPlannedSessions) errors.formPlannedSessions = "Required";
    if (!formPlannedUnits) errors.formPlannedUnits = "Required";
    this.setState({ formError: errors });
    return Object.keys(errors).length === 0;
  };

  saveRecord = async () => {
    if (!this.validateForm()) return;
    const { formCenter, formCenterSite, formDate, formPlannedSessions, formPlannedUnits, editRecord } = this.state;

    this.setState({ isSaving: true });
    try {
      const body = {
        center: formCenter.value,
        centerSite: formCenterSite?.value || null,
        date: formDate,
        plannedSessions: Number(formPlannedSessions),
        plannedUnits: Number(formPlannedUnits),
      };

      if (editRecord) {
        body.id = editRecord._id;
        await updateBloodCollectionDailyPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan updated");
      } else {
        await createBloodCollectionDailyPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan created");
      }

      this.closeModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSaving: false });
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDeleteModal = () => this.setState({ showDeleteModal: false, deleteId: null });

  deleteRecord = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteBloodCollectionDailyPlan(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Plan deleted");
      this.closeDeleteModal();
      this.fetchPlans();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  renderCalendar() {
    const { calYear, calMonth, isLoading, isLoadingPerformed } = this.state;
    const planMap = this.plansByDate();

    const firstOfMonth = moment(`${calYear}-${String(calMonth).padStart(2, "0")}-01`);
    const daysInMonth = firstOfMonth.daysInMonth();
    // Monday = 0, ..., Sunday = 6
    const startDow = (firstOfMonth.day() + 6) % 7;

    const todayStr = moment().format("YYYY-MM-DD");

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
      const week = cells.slice(i, i + 7);
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }

    return (
      <div style={calContainerStyle}>
        {/* Month navigation header */}
        <div style={calHeaderStyle}>
          <button onClick={() => this.navigateMonth(-1)} style={navBtnStyle} title="Previous month">&#8249;</button>
          <div style={{ fontWeight: "bold", fontSize: 18, letterSpacing: 0.3 }}>
            {MONTH_NAMES[calMonth - 1]} {calYear}
          </div>
          <button onClick={() => this.navigateMonth(1)} style={navBtnStyle} title="Next month">&#8250;</button>
        </div>

        {isLoading && (
          <div style={{ textAlign: "center", padding: "20px 0", color: "#888", fontSize: 13 }}>
            Loading plans...
          </div>
        )}

        {isLoadingPerformed && !isLoading && (
          <div style={{ textAlign: "center", fontSize: 11, color: "#888", marginBottom: 6 }}>
            Fetching performed data from donation records...
          </div>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr>
              {DAY_HEADERS.map((d) => (
                <th key={d} style={calThStyle}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((day, di) => {
                  if (!day) {
                    return <td key={di} style={calEmptyCellStyle} />;
                  }

                  const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const dayPlans = planMap[dateStr] || [];
                  const isToday = dateStr === todayStr;
                  const isWeekend = di >= 5; // Sat=5, Sun=6

                  const totalPlannedSess = dayPlans.reduce((s, p) => s + (p.plannedSessions || 0), 0);
                  const totalPlannedUnits = dayPlans.reduce((s, p) => s + (p.plannedUnits || 0), 0);
                  const totalPerformed = dayPlans.reduce((s, p) => s + (this.getPerformed(p) || 0), 0);
                  const achievedPct = totalPlannedSess > 0
                    ? Math.round((totalPerformed / totalPlannedSess) * 100)
                    : null;

                  return (
                    <td
                      key={di}
                      style={calDayCellStyle(isToday, isWeekend, dayPlans.length > 0)}
                      onClick={() => dayPlans.length > 0 && this.openDayModal(dateStr, dayPlans)}
                      title={dayPlans.length > 0 ? "Click to view plans" : "No plan for this day"}
                    >
                      <div style={calDayNumStyle(isToday)}>{day}</div>

                      {dayPlans.length > 0 && (
                        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                          {/* Planned badge */}
                          <div style={calBadge("#1a5276", "#d6eaf8")}>
                            {totalPlannedSess} sess &bull; {totalPlannedUnits} units
                          </div>

                          {/* Performed badge */}
                          {isLoadingPerformed ? (
                            <div style={calBadge("#777", "#f0f0f0")}>Pf: ...</div>
                          ) : (
                            <div style={calBadge(
                              achievedPct === null ? "#777" :
                                achievedPct >= 100 ? "#1a7c3e" :
                                  achievedPct > 0 ? "#c0392b" : "#777",
                              achievedPct === null ? "#f0f0f0" :
                                achievedPct >= 100 ? "#d5f5e3" :
                                  achievedPct > 0 ? "#fdecea" : "#f0f0f0"
                            )}>
                              Pf: {totalPerformed}
                              {achievedPct !== null && ` (${achievedPct}%)`}
                            </div>
                          )}

                          {/* Site count */}
                          {dayPlans.length > 1 && (
                            <div style={{ fontSize: 9, color: "#888", textAlign: "center" }}>
                              {dayPlans.length} sites
                            </div>
                          )}
                        </div>
                      )}

                      {/* + button for days with no plans */}
                      {dayPlans.length === 0 && (
                        <div
                          style={calAddBtnStyle}
                          onClick={(e) => { e.stopPropagation(); this.openAddModal(dateStr); }}
                          title="Add plan for this day"
                        >
                          +
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Legend */}
        <div style={{ marginTop: 10, display: "flex", gap: 16, fontSize: 11, color: "#555", flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: "#d6eaf8", border: "1px solid #1a5276", borderRadius: 2 }} />
            Planned
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: "#d5f5e3", border: "1px solid #1a7c3e", borderRadius: 2 }} />
            Performed &ge; 100%
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: "#fdecea", border: "1px solid #c0392b", borderRadius: 2 }} />
            Below target
          </span>
          <span style={{ color: "#888" }}>
            Performed pulled automatically from blood donation records &bull; Click a day to see details
          </span>
        </div>
      </div>
    );
  }

  renderListView() {
    const { plans, isLoading, isLoadingPerformed } = this.state;
    if (isLoading) return <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>;
    if (!plans.length) return (
      <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
        No plans found for this month. Click "+ Add Plan" to create one.
      </div>
    );

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Day</th>
              <th style={thStyle}>Wk</th>
              <th style={thStyle}>Center</th>
              <th style={thStyle}>Site</th>
              <th style={thStyle}>Pl. Sessions</th>
              <th style={thStyle}>Pf. Sessions</th>
              <th style={thStyle}>Pl. Units</th>
              <th style={thStyle}>Achievement</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const pf = this.getPerformed(p);
              const pfDisplay = isLoadingPerformed ? "…" : (pf ?? "—");
              const pct = p.plannedSessions > 0 && pf != null
                ? Math.round((pf / p.plannedSessions) * 100)
                : null;

              return (
                <tr key={p._id}>
                  <td style={tdStyle}>{moment(p.date).format("DD/MM/YYYY")}</td>
                  <td style={tdStyle}>{p.dayOfWeek || moment(p.date).format("ddd")}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>W{p.week}</td>
                  <td style={tdStyle}>{p.center?.name || "—"}</td>
                  <td style={tdStyle}>{p.centerSite?.name || <em style={{ color: "#888", fontSize: 11 }}>All sites</em>}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedSessions}</td>
                  <td style={pfCellStyle(pf, p.plannedSessions)}>{pfDisplay}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedUnits}</td>
                  <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold",
                    color: pct === null ? "#888" : pct >= 100 ? "#1a7c3e" : pct >= 80 ? "#e67e22" : "#c0392b" }}>
                    {pct !== null ? `${pct}%` : "—"}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button onClick={() => this.openEditModal(p)} style={actionBtnStyle("#2980b9")}>Edit</button>
                    {" "}
                    <button onClick={() => this.confirmDelete(p._id)} style={actionBtnStyle("#c0392b")}>Del</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderDayModal() {
    const { showDayModal, selectedDay } = this.state;
    if (!showDayModal || !selectedDay) return null;
    const { dateStr, plans } = selectedDay;

    return (
      <Modal
        show={showDayModal}
        handleClose={this.closeDayModal}
        title={`Daily Plan — ${moment(dateStr).format("dddd, MMMM D, YYYY")}`}
        size="md"
      >
        <div className="modal-body">
          {plans.length === 0 ? (
            <div style={{ color: "#888", textAlign: "center", padding: 16 }}>No plans for this day.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f0f0f0" }}>
                  <th style={thStyle}>Center</th>
                  <th style={thStyle}>Site</th>
                  <th style={thStyle}>Pl. Sessions</th>
                  <th style={thStyle}>Pl. Units</th>
                  <th style={thStyle}>Performed</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => {
                  const pf = this.getPerformed(p);
                  const pct = p.plannedSessions > 0 && pf != null
                    ? Math.round((pf / p.plannedSessions) * 100)
                    : null;
                  return (
                    <tr key={p._id}>
                      <td style={tdStyle}>{p.center?.name || "—"}</td>
                      <td style={tdStyle}>{p.centerSite?.name || <em style={{ color: "#888", fontSize: 11 }}>All sites</em>}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedSessions}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedUnits}</td>
                      <td style={{
                        ...tdStyle, textAlign: "center", fontWeight: "bold",
                        color: pf == null ? "#aaa" : pf >= p.plannedSessions && p.plannedSessions > 0 ? "#1a7c3e" : pf > 0 ? "#c0392b" : "#888",
                      }}>
                        {pf ?? "—"}
                        {pct !== null && <span style={{ fontSize: 10, marginLeft: 4 }}>({pct}%)</span>}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <button
                          onClick={() => { this.closeDayModal(); this.openEditModal(p); }}
                          style={actionBtnStyle("#2980b9")}
                        >
                          Edit
                        </button>
                        {" "}
                        <button
                          onClick={() => { this.closeDayModal(); this.confirmDelete(p._id); }}
                          style={actionBtnStyle("#c0392b")}
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 12, padding: "8px 12px", background: "#e8f4fd", borderRadius: 6, fontSize: 11, color: "#555" }}>
            Performed data is fetched automatically from blood donation records in BBMS.
          </div>
        </div>
        <div className="modal-footer">
          <Button text="Close" onPress={this.closeDayModal} className="btn-default" />
          <Button
            text="+ Add Plan for this Day"
            onPress={() => { this.closeDayModal(); this.openAddModal(dateStr); }}
          />
        </div>
      </Modal>
    );
  }

  render() {
    const {
      isAdmin,
      calYear, calMonth,
      filterCenter, filterCenterSite,
      centers, centerSites,
      showModal, showDeleteModal, editRecord,
      formCenter, formCenterSite, formCenterSites, formDate,
      formPlannedSessions, formPlannedUnits, formError, isSaving, isDeleting,
      viewMode,
    } = this.state;

    return (
      <div>
        {/* Toolbar */}
        <div className="no-print row" style={{ marginBottom: 14, alignItems: "flex-end" }}>
          {/* Year quick-jump */}
          <div className="col-md-2">
            <Select
              label="Year"
              options={YEARS}
              value={YEARS.find((y) => y.value === calYear) || null}
              onChange={(v) => {
                if (v?.value) this.setState({ calYear: v.value }, this.fetchPlans);
              }}
            />
          </div>

          {/* Admin: center / site filters */}
          {isAdmin ? (
            <>
              <div className="col-md-2">
                <Select
                  label="Center"
                  options={centers}
                  value={filterCenter}
                  onChange={(v) => {
                    this.setState({ filterCenter: v, filterCenterSite: null, centerSites: [] });
                    if (v?.value) this.getCenterSites(v.value);
                  }}
                  isClearable
                />
              </div>
              <div className="col-md-2">
                <Select
                  label="Site"
                  options={centerSites}
                  value={filterCenterSite}
                  onChange={(v) => this.setState({ filterCenterSite: v })}
                  isDisabled={!filterCenter}
                  isClearable
                />
              </div>
            </>
          ) : (
            <div className="col-md-4" style={{ display: "flex", alignItems: "flex-end" }}>
              <div style={roleTagStyle}>Viewing: your site only</div>
            </div>
          )}

          <div className="col-md-4" style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <Button text="Search" onPress={this.fetchPlans} />
            <Button text="+ Add Plan" onPress={() => this.openAddModal(null)} className="btn-default" />
          </div>
        </div>

        {/* Title bar with month navigation */}
        <div style={monthNavBarStyle}>
          <button onClick={() => this.navigateMonth(-1)} style={navBtnStyleBar} title="Previous month">&#8249;</button>
          <span style={{ fontWeight: "bold", fontSize: 15, minWidth: 160, textAlign: "center" }}>
            {MONTH_NAMES[calMonth - 1]} {calYear}
          </span>
          <button onClick={() => this.navigateMonth(1)} style={navBtnStyleBar} title="Next month">&#8250;</button>

          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button
              onClick={() => this.setState({ viewMode: "calendar" })}
              style={{ ...toggleBtn, ...(viewMode === "calendar" ? toggleBtnActive : {}) }}
            >
              Calendar
            </button>
            <button
              onClick={() => this.setState({ viewMode: "list" })}
              style={{ ...toggleBtn, ...(viewMode === "list" ? toggleBtnActive : {}) }}
            >
              List
            </button>
          </div>
        </div>

        {/* Main content */}
        {viewMode === "calendar" ? this.renderCalendar() : this.renderListView()}

        {/* Day detail modal */}
        {this.renderDayModal()}

        {/* Add / Edit Modal */}
        <Modal
          show={showModal}
          handleClose={this.closeModal}
          title={editRecord ? "Edit Daily Plan" : "Add Daily Plan"}
          size="md"
        >
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <Select
                  label="Center"
                  options={centers}
                  value={formCenter}
                  onChange={(v) => {
                    this.setState({ formCenter: v, formCenterSite: null, formCenterSites: [] });
                    if (v?.value) this.getFormCenterSites(v.value);
                  }}
                  isDisabled={!isAdmin}
                  error={formError.formCenter}
                />
              </div>
              <div className="col-md-12">
                <Select
                  label="Center Site (optional — leave blank for center-level plan)"
                  options={formCenterSites}
                  value={formCenterSite}
                  onChange={(v) => this.setState({ formCenterSite: v })}
                  isDisabled={!formCenter || !isAdmin}
                  isClearable
                  error={formError.formCenterSite}
                />
              </div>
              <div className="col-md-12">
                <Input
                  label="Date"
                  type="date"
                  value={formDate}
                  onChange={(e) => this.setState({ formDate: e.target.value })}
                  error={formError.formDate}
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Planned Sessions"
                  type="number"
                  value={formPlannedSessions}
                  onChange={(e) => this.setState({ formPlannedSessions: e.target.value })}
                  error={formError.formPlannedSessions}
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Planned Units"
                  type="number"
                  value={formPlannedUnits}
                  onChange={(e) => this.setState({ formPlannedUnits: e.target.value })}
                  error={formError.formPlannedUnits}
                />
              </div>
            </div>
            <div style={{ marginTop: 10, padding: "8px 12px", background: "#e8f4fd", borderRadius: 6, fontSize: 12, color: "#555" }}>
              Performed data is pulled automatically from blood donation records in the system.
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closeModal} className="btn-default" />
            <Button text={editRecord ? "Update" : "Save"} onPress={this.saveRecord} isSubmitting={isSaving} />
          </div>
        </Modal>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} handleClose={this.closeDeleteModal} title="Delete Plan">
          <DeleteModal
            handleCloseModal={this.closeDeleteModal}
            onDelete={this.deleteRecord}
            isDeleting={isDeleting}
          />
        </Modal>
      </div>
    );
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const calContainerStyle = {
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
  padding: "16px",
};

const calHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};

const monthNavBarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
  padding: "8px 12px",
  background: "#f8f9fa",
  borderRadius: 6,
  border: "1px solid #e0e0e0",
};

const navBtnStyle = {
  background: "#003580",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  width: 32,
  height: 32,
  fontSize: 20,
  lineHeight: 1,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const navBtnStyleBar = {
  ...navBtnStyle,
  background: "transparent",
  color: "#003580",
  border: "1px solid #003580",
};

const calThStyle = {
  padding: "8px 4px",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 12,
  color: "#555",
  borderBottom: "2px solid #e0e0e0",
  background: "#f8f9fa",
};

const calDayCellStyle = (isToday, isWeekend, hasPlans) => ({
  verticalAlign: "top",
  padding: "6px 5px",
  border: "1px solid #e8e8e8",
  background: isToday ? "#fff8e1" : isWeekend ? "#fafafa" : "#fff",
  cursor: hasPlans ? "pointer" : "default",
  minHeight: 80,
  transition: "background 0.15s",
  position: "relative",
});

const calEmptyCellStyle = {
  border: "1px solid #f0f0f0",
  background: "#f9f9f9",
};

const calDayNumStyle = (isToday) => ({
  fontWeight: isToday ? "bold" : "normal",
  fontSize: 13,
  color: isToday ? "#fff" : "#333",
  background: isToday ? "#003580" : "transparent",
  borderRadius: isToday ? "50%" : 0,
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 2,
});

const calBadge = (textColor, bgColor) => ({
  fontSize: 10,
  color: textColor,
  background: bgColor,
  borderRadius: 3,
  padding: "2px 5px",
  border: `1px solid ${textColor}`,
  lineHeight: 1.4,
  wordBreak: "break-word",
});

const calAddBtnStyle = {
  position: "absolute",
  bottom: 4,
  right: 4,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: "#e0e0e0",
  color: "#666",
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  lineHeight: 1,
};

const thStyle = {
  border: "1px solid #ccc",
  padding: "6px 8px",
  textAlign: "left",
  fontWeight: "bold",
  fontSize: 12,
  whiteSpace: "nowrap",
};

const tdStyle = {
  border: "1px solid #ddd",
  padding: "5px 8px",
  whiteSpace: "nowrap",
};

const pfCellStyle = (pf, planned) => ({
  ...tdStyle,
  textAlign: "center",
  color: pf == null ? "#aaa" : pf >= planned && planned > 0 ? "#1a7c3e" : pf > 0 ? "#c0392b" : "#888",
});

const actionBtnStyle = (color) => ({
  background: color,
  color: "#fff",
  border: "none",
  borderRadius: 3,
  padding: "3px 8px",
  cursor: "pointer",
  fontSize: 11,
});

const toggleBtn = {
  padding: "5px 14px",
  borderRadius: 4,
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer",
  fontSize: 12,
};

const toggleBtnActive = {
  background: "#003580",
  color: "#fff",
  borderColor: "#003580",
};

const roleTagStyle = {
  background: "#e8f4fd",
  border: "1px solid #b3d7f5",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 12,
  color: "#1a5276",
  display: "inline-flex",
  alignItems: "center",
};

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(BloodCollectionDailyPlan);
